// Hardcoded case studies of the founder's three operating businesses.
// They power the public /case-study and /case-study/:slug pages and are
// imported on both the client (page rendering) and server (SEO meta +
// JSON-LD + sitemap). Keep this file dependency-free (NO @assets imports
// — the server bundle has no Vite asset resolver) so it stays usable
// from both contexts. Hashed client-side image URLs live in
// client/src/lib/case-study-images.ts; the absolute paths below are used
// by the server for OG/JSON-LD canonical URLs.
//
// When the founder ships final photos and metrics, edit the entries
// below (and replace the matching files under
// attached_assets/generated_images/) and rebuild.

export interface CaseStudyMetric {
  /** Big headline number, e.g. "70%". Keep it short — it renders large. */
  value: string;
  /** One-liner explaining what the number measures. */
  label: string;
  /** Optional context (compared-to/period). */
  description?: string;
}

/** Single bubble in the WhatsApp mockup. Mirrors the shape accepted by
 *  client/src/components/whatsapp-chat-mockup.tsx so we can pass these
 *  straight through. Defined here (and not imported from /client) so
 *  shared/case-studies.ts stays server-safe. */
export type CaseStudyChatMessage =
  | { from: "user"; text: string; time: string; kind?: "text" }
  | { from: "user"; time: string; kind: "voice"; duration: string }
  | { from: "user"; time: string; kind: "image"; caption: string }
  | { from: "business"; text: string; time: string; kind?: "text" };

export interface CaseStudyChatExample {
  /** Title shown in the mock WhatsApp header. */
  title: string;
  /** Optional subtitle (typically "online" or module name). */
  subtitle: string;
  /** 3-5 alternating user/business bubbles. */
  messages: CaseStudyChatMessage[];
}

export interface CaseStudy {
  slug: string;
  name: string;
  sector: string;
  tagline: string;
  /** Public path used by the server to build absolute OG image URLs. The
   *  hashed client URL is resolved via case-study-images.ts on the front
   *  end. Must match an existing file under attached_assets so prerender
   *  + share previews can reach it. */
  heroImagePublicPath: string;
  /** Variante WebP mostrata nelle pagine; il PNG resta per og:image (compatibilità social). */
  heroImageDisplayPath: string;
  /** Caption for the hero image, used as alt text for accessibility. */
  heroAlt: string;
  /** When the founder started using the ERP at this business. */
  startedOn: string;
  /** Italian-region label (e.g. "Roma, Lazio") — small print under hero. */
  location: string;
  /** Short, hardened paragraphs framing the pre-ERP pain. */
  problem: string[];
  /** Modules / capabilities that were turned on for this business. */
  solutionModules: { title: string; detail: string }[];
  /** A second paragraph block describing the change after the ERP. */
  solutionStory: string[];
  /** 3–4 headline metrics; render in big cards on the page. */
  results: CaseStudyMetric[];
  /** Long founder quote, third-person voice avoided — first person. */
  quote: string;
  /** What the founder is measuring next. Optional. */
  nextStep?: string;
  /** Sample WhatsApp exchange shown in the "La soluzione" section to make
   *  the modules tangible (not screenshots — interactive mockup powered
   *  by client/src/components/whatsapp-chat-mockup.tsx). */
  whatsappExample: CaseStudyChatExample;
  /** SEO description used in <meta description> + share previews. */
  seoDescription: string;
}

export const CASE_STUDIES: readonly CaseStudy[] = [
  {
    slug: "ab-studio-consulenza",
    name: "AB Studio",
    sector: "Consulenza strategica per PMI",
    tagline:
      "Lo studio di consulenza che ha smesso di lavorare la sera per chiudere i conti.",
    heroImagePublicPath: "/case-study/case-study-consulenza.png",
    heroImageDisplayPath: "/case-study/case-study-consulenza.webp",
    heroAlt:
      "Due consulenti analizzano dati su un laptop nello studio AB Studio, Roma.",
    startedOn: "Marzo 2024",
    location: "Roma, Lazio",
    problem: [
      "AB Studio fattura ai clienti a progetto, ma il vero problema non era emettere le fatture: era ricordarsi quali progetti erano davvero chiusi e quali ancora aperti. I due soci usavano fogli Excel diversi, calendari condivisi a metà e una WhatsApp di gruppo che diventava il vero gestionale di fatto.",
      "A fine mese serviva mezza giornata solo per capire quanti soldi sarebbero entrati il mese successivo. Spesso una fattura partiva con due settimane di ritardo perché nessuno se n'era accorto.",
    ],
    solutionModules: [
      {
        title: "Pipeline progetti",
        detail:
          "Ogni progetto attivo ha uno stato (in corso, da fatturare, incassato). Si aggiorna scrivendo in chat \"Progetto X chiuso\" e basta.",
      },
      {
        title: "Riepilogo incassi",
        detail:
          "Ogni mattina alle 8:00 il bot manda in chat: incassato ieri, in scadenza questa settimana, fatture partite e non saldate.",
      },
      {
        title: "Reminder fatturazione",
        detail:
          "Quando un progetto chiude, il sistema chiede via WhatsApp se preparare la fattura. Si risponde sì e parte tutto.",
      },
    ],
    solutionStory: [
      "Otto settimane dopo il setup, AB Studio ha smesso di tenere lo \"stato progetti\" in testa. La sera nessuno apre più Excel per controllare. La domenica si risponde via WhatsApp \"sì, incassato\" e il sistema chiude il giro.",
      "I due soci ora usano la riunione del lunedì per parlare di clienti e proposte, non per allinearsi su chi ha fatturato cosa.",
    ],
    results: [
      {
        value: "−12 ore",
        label: "al mese sui controlli",
        description: "Tempo prima passato a riconciliare fogli e calendari.",
      },
      {
        value: "0",
        label: "fatture dimenticate",
        description: "Nei primi 6 mesi dopo l'attivazione del modulo reminder.",
      },
      {
        value: "+18 gg",
        label: "incasso medio anticipato",
        description:
          "Le fatture partono il giorno della chiusura progetto, non a fine mese.",
      },
      {
        value: "5 sec",
        label: "per sapere il fatturato del mese",
        description: "Una domanda in chat, una riga di risposta.",
      },
    ],
    quote:
      "Per anni mi sono detto che un consulente non ha bisogno di un gestionale, basta un Excel. Avevo torto. Quello che mi serviva non era un altro software da aprire, era qualcuno che mi rispondesse \"Sì, ti hanno pagato\" senza dover guardare. Adesso lo fa il sistema, su WhatsApp, mentre sono dal cliente.",
    nextStep:
      "Aggiungere un modulo per la gestione delle proposte commerciali in pipeline.",
    whatsappExample: {
      title: "AB Studio · Operations",
      subtitle: "Bot ERP · online",
      messages: [
        {
          from: "user",
          text: "Progetto Bianchi chiuso, fattura mensile",
          time: "09:14",
        },
        {
          from: "business",
          text: "Perfetto. Preparo fattura n° 2024/078 a Bianchi SRL · €4.800 + IVA. Confermi?",
          time: "09:14",
        },
        {
          from: "user",
          text: "Sì manda",
          time: "09:15",
        },
        {
          from: "business",
          text: "Inviata. Aggiunta allo storico. Saldo previsto entro 30/04.",
          time: "09:15",
        },
      ],
    },
    seoDescription:
      "Caso studio AB Studio: come uno studio di consulenza per PMI ha eliminato 12 ore al mese di controlli usando il nostro ERP conversazionale su WhatsApp.",
  },
  {
    slug: "centrale-forniture",
    name: "Centrale Forniture",
    sector: "Distribuzione di forniture per ufficio",
    tagline: "Il magazzino che parla. Ogni mattina sa già cosa sta finendo.",
    heroImagePublicPath: "/case-study/case-study-commercio.png",
    heroImageDisplayPath: "/case-study/case-study-commercio.webp",
    heroAlt:
      "Magazzino di Centrale Forniture: scaffali ordinati e responsabile che controlla un tablet.",
    startedOn: "Gennaio 2024",
    location: "Provincia di Roma",
    problem: [
      "Centrale Forniture vende forniture per uffici a partita IVA in tutto il Lazio. Tre persone in magazzino, una al telefono per gli ordini, un commerciale fuori. Il problema non era vendere: era sapere — adesso, in questo momento — quanto c'era a magazzino di un articolo.",
      "Quando un cliente chiamava per chiedere se c'era disponibile un certo modello di sedia, qualcuno doveva alzarsi, andare a guardare e richiamare. Spesso si vendeva qualcosa che non c'era più, e si scoprivano gli ammanchi solo quando finiva sotto soglia.",
    ],
    solutionModules: [
      {
        title: "Inventario in tempo reale",
        detail:
          "Ogni movimento (carico, scarico, vendita) si registra con un messaggio veloce. Il bot tiene il conto, non serve un PC.",
      },
      {
        title: "Soglie di riordino",
        detail:
          "Quando un prodotto scende sotto soglia, parte un messaggio in chat al titolare con quantità da riordinare e fornitore.",
      },
      {
        title: "Disponibilità via WhatsApp",
        detail:
          "Chi è al telefono col cliente può chiedere \"quanti ABCD123 abbiamo?\" e ricevere risposta in due secondi.",
      },
    ],
    solutionStory: [
      "Dopo due mesi, le telefonate al magazzino per chiedere giacenze sono praticamente sparite. La persona al telefono col cliente conferma in tempo reale, senza mettere in attesa.",
      "Il riordino merci non si fa più \"a sensazione\" il lunedì mattina: arriva un messaggio quando serve, con il numero giusto. Le rotture di stock sui prodotti top sono crollate.",
    ],
    results: [
      {
        value: "−85%",
        label: "rotture di stock",
        description: "Sui 50 prodotti più venduti, prima vs dopo 6 mesi.",
      },
      {
        value: "+22%",
        label: "ordini chiusi al primo contatto",
        description:
          "Il cliente non viene più richiamato dopo aver verificato la giacenza.",
      },
      {
        value: "0 PC",
        label: "aperti in magazzino",
        description: "Tutto avviene da telefono, anche con i guanti.",
      },
      {
        value: "30 min",
        label: "al giorno di telefonate evitate",
        description: "Tra ufficio e magazzino, per chiedere disponibilità.",
      },
    ],
    quote:
      "Avevamo provato due gestionali \"da PMI\". Bellissimi a vederli, ma in magazzino nessuno li apriva, e quindi i dati erano sempre vecchi di mezza giornata. Con WhatsApp il magazzino lo aggiorna chi è in magazzino, mentre lo aggiorna. È l'unica cosa che ha funzionato davvero.",
    nextStep:
      "Collegare le bolle di trasporto in modo che il vettore aggiorni la consegna direttamente in chat.",
    whatsappExample: {
      title: "Centrale Forniture · Magazzino",
      subtitle: "Inventario in tempo reale",
      messages: [
        {
          from: "user",
          text: "Quante risme A4 80g abbiamo?",
          time: "10:32",
        },
        {
          from: "business",
          text: "142 risme. Sotto soglia minima (200). Vuoi che ordini al fornitore Cartiera Lazio?",
          time: "10:32",
        },
        {
          from: "user",
          text: "Sì, 500",
          time: "10:33",
        },
        {
          from: "business",
          text: "Ordine 500 risme inviato a Cartiera Lazio. Consegna prevista venerdì 02/05.",
          time: "10:33",
        },
      ],
    },
    seoDescription:
      "Caso studio Centrale Forniture: come una società di distribuzione ha azzerato le rotture di stock e velocizzato le risposte ai clienti con il nostro ERP su WhatsApp.",
  },
  {
    slug: "tre-pini-immobiliare",
    name: "Tre Pini Real Estate",
    sector: "Investimenti e gestione immobiliare",
    tagline:
      "Tre cantieri, dieci affitti, una sola WhatsApp. Tutto sotto controllo.",
    heroImagePublicPath: "/case-study/case-study-immobiliare.png",
    heroImageDisplayPath: "/case-study/case-study-immobiliare.webp",
    heroAlt:
      "Ufficio Tre Pini Real Estate a Roma con planimetrie su un tavolo di vetro.",
    startedOn: "Settembre 2023",
    location: "Roma e provincia",
    problem: [
      "Tre Pini gestisce dieci immobili in affitto e segue due o tre piccole ristrutturazioni in parallelo. Il titolare passa in cantiere, parla con gli inquilini, firma da un notaio. Il \"gestionale\" era una cartellina di Drive con dieci sotto-cartelle e una rubrica piena di fornitori.",
      "I problemi erano due: 1) sapere ogni mese chi aveva pagato l'affitto e chi no, senza dover aprire dieci file; 2) tenere traccia delle scadenze (assicurazioni, manutenzioni, certificazioni APE) senza lasciarne sfuggire una.",
    ],
    solutionModules: [
      {
        title: "Gestione affitti",
        detail:
          "A inizio mese il bot manda in chat l'elenco affitti attesi. Mano a mano che arrivano, si segnala \"pagato\" e il sistema aggiorna lo storico.",
      },
      {
        title: "Calendario scadenze",
        detail:
          "Tutte le scadenze (assicurazioni, APE, revisioni caldaia) sono tracciate. Reminder 30/15/3 giorni prima, su WhatsApp.",
      },
      {
        title: "Stato cantieri",
        detail:
          "Per ogni cantiere: budget, speso, prossima milestone. Si chiede in chat \"come va via X?\" e si riceve la sintesi.",
      },
    ],
    solutionStory: [
      "Dopo sei mesi, il titolare non ha più bisogno di sedersi al PC la prima settimana del mese per fare il giro pagamenti. Risponde \"pagato\" man mano che arrivano i bonifici, dal telefono.",
      "Le scadenze non vanno più perse: la prima volta che è arrivato il reminder per la revisione caldaia di un appartamento ha pagato da solo l'investimento di sei mesi del sistema.",
    ],
    results: [
      {
        value: "100%",
        label: "scadenze rispettate",
        description: "12 mesi consecutivi senza una scadenza dimenticata.",
      },
      {
        value: "−6 ore",
        label: "al mese sulla gestione affitti",
        description: "Tempo prima passato a riconciliare bonifici e file.",
      },
      {
        value: "3",
        label: "cantieri in parallelo",
        description: "Gestiti senza assumere un project manager dedicato.",
      },
      {
        value: "1",
        label: "WhatsApp al posto di 10 cartelle",
        description: "Tutto lo storico è interrogabile dalla chat.",
      },
    ],
    quote:
      "Le scadenze sono il mio incubo da sempre. Con dieci immobili e tre cantieri, ne perdi una al mese. Adesso, quando arriva il messaggio \"caldaia di via X scade fra 30 giorni\", io rispondo \"chiama il tecnico\" e il sistema apre la pratica. È come avere un assistente, ma sta dentro WhatsApp e non si ammala.",
    nextStep:
      "Integrare le richieste degli inquilini (perdite, guasti) come ticket conversazionali.",
    whatsappExample: {
      title: "Tre Pini · Operations",
      subtitle: "Bot ERP · online",
      messages: [
        {
          from: "business",
          text: "Promemoria: APE Via Garibaldi 12 scade fra 30 giorni (28/05). Vuoi che chieda preventivo a Geom. Russo?",
          time: "08:00",
        },
        {
          from: "user",
          text: "Sì, e fissa appuntamento",
          time: "08:02",
        },
        {
          from: "business",
          text: "Preventivo richiesto. Russo disponibile martedì 06/05 alle 10:00.",
          time: "08:03",
        },
        {
          from: "user",
          text: "Bene. Pagato affitto Bianchi marzo",
          time: "08:04",
        },
        {
          from: "business",
          text: "Registrato. 9/10 affitti pagati questo mese. Resta solo Verdi.",
          time: "08:04",
        },
      ],
    },
    seoDescription:
      "Caso studio Tre Pini Real Estate: come un investitore immobiliare gestisce dieci affitti e tre cantieri da WhatsApp, senza mai mancare una scadenza.",
  },
] as const;

export type CaseStudySlug = (typeof CASE_STUDIES)[number]["slug"];

export const CASE_STUDY_SLUGS: readonly string[] = CASE_STUDIES.map(
  (c) => c.slug,
);

export function getCaseStudyBySlug(slug: string): CaseStudy | undefined {
  return CASE_STUDIES.find((c) => c.slug === slug);
}

/** Only approved entries may be linked or described on public surfaces. */
export function publishedCaseStudies(
  isVerified: (key: string) => boolean,
): CaseStudy[] {
  return CASE_STUDIES.filter((study) => isVerified(`case-study.${study.slug}`));
}

export const CASE_STUDY_INDEX_COPY = {
  title: "Case study ERP conversazionale | AB MARK",
  description: "Esplora i casi studio approvati sull'ERP conversazionale di AB MARK.",
  emptyDescription: "I casi studio sono in fase di verifica. Scopri come funziona l'ERP conversazionale o contattaci per parlare del tuo progetto.",
  heading: "Casi studio dell'ERP conversazionale",
  emptyMessage: "Stiamo verificando le storie e i risultati prima di pubblicarli. Torna a trovarci; nel frattempo puoi scoprire come funziona l'ERP conversazionale.",
} as const;
